import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FonotekaPack } from '@/api/client';
import { api } from '@/api/client';
import { FONOTEKA_QUERY_KEY } from '@/features/fonoteka/queryKeys';
import { AUDIO_FADE_MS, AUDIO_GAIN_FADE_MS, fadeAudioVolume } from './audioElementFade';
import { buildFonotekaPlaylist, fonotekaPlaylistIndex, neighborInPlaylist, playlistIndex } from './fonotekaPlaylist';
import {
  effectiveAudioVolume,
  loadGlobalAudioVolumePrefs,
  saveGlobalAudioVolumePrefs,
} from './globalAudioVolumeStorage';
import {
  reduceGlobalAudio,
  shouldToggleCurrent,
  type GlobalAudioPlaylist,
  type GlobalAudioTrack,
} from './globalAudioState';

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

export type GlobalAudioPlaybackApi = {
  track: GlobalAudioTrack | null;
  isPlaying: boolean;
  canPlayPrevious: boolean;
  canPlayNext: boolean;
  /** playlist — откуда включили (блок «Фонотека» карточки); нет — соседи из всей Фонотеки */
  play: (track: GlobalAudioTrack, opts?: { playlist?: GlobalAudioPlaylist }) => void;
  toggle: () => void;
  playPrevious: () => void;
  playNext: () => void;
  seek: (ratio: number) => void;
  /** Duck output before scrubbing so seeks do not click. */
  beginSeekSilence: () => void;
  /** Restore user volume after scrubbing. */
  endSeekSilence: () => void;
  stop: () => void;
};

export type GlobalAudioClockApi = {
  currentTimeSec: number;
  durationSec: number;
  progressRatio: number;
};

export type GlobalAudioVolumeApi = {
  volume: number;
  muted: boolean;
  setVolume: (volume: number, opts?: { ramp?: boolean }) => void;
  toggleMute: () => void;
};

/** @deprecated Prefer useGlobalAudioPlayback / Clock / Volume to avoid broad re-renders. */
export type GlobalAudioApi = GlobalAudioPlaybackApi & GlobalAudioClockApi & GlobalAudioVolumeApi;

const GlobalAudioPlaybackContext = createContext<GlobalAudioPlaybackApi | null>(null);
const GlobalAudioClockContext = createContext<GlobalAudioClockApi | null>(null);
const GlobalAudioVolumeContext = createContext<GlobalAudioVolumeApi | null>(null);

export function GlobalAudioProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: packs } = useQuery({
    queryKey: FONOTEKA_QUERY_KEY,
    queryFn: () => api.fonoteka.listPacks(),
    staleTime: Infinity,
    /** Cache-only: catalog/picker owners fetch; avoids unauthorized calls on every page. */
    enabled: false,
  });
  const packsRef = useRef<FonotekaPack[] | undefined>(packs);
  packsRef.current = packs;

  const initialVolume = useMemo(() => loadGlobalAudioVolumePrefs(), []);
  const [volume, setVolumeState] = useState(initialVolume.volume);
  const [muted, setMutedState] = useState(initialVolume.muted);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  volumeRef.current = volume;
  mutedRef.current = muted;

  const [state, dispatch] = useReducer(reduceGlobalAudio, { track: null });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<GlobalAudioTrack | null>(null);
  const playRef = useRef<(track: GlobalAudioTrack, opts?: { force?: boolean }) => void>(() => {});
  /** Плейлист, из которого включили; null — вся Фонотека. Соседи (force) его не меняют */
  const playlistRef = useRef<GlobalAudioPlaylist | null>(null);
  const [playlist, setPlaylist] = useState<GlobalAudioPlaylist | null>(null);
  const opTokenRef = useRef(0);
  const gainTokenRef = useRef(0);
  const seekSilentRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);

  trackRef.current = state.track;

  const beginOp = useCallback(() => {
    opTokenRef.current += 1;
    gainTokenRef.current += 1;
    return opTokenRef.current;
  }, []);

  const isOpActive = useCallback((token: number) => token === opTokenRef.current, []);

  const beginGainOp = useCallback(() => {
    gainTokenRef.current += 1;
    return gainTokenRef.current;
  }, []);

  const isGainActive = useCallback((token: number) => token === gainTokenRef.current, []);

  const readOutputVolume = useCallback(
    () => effectiveAudioVolume({ volume: volumeRef.current, muted: mutedRef.current }),
    [],
  );

  const applyOutputVolume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || seekSilentRef.current) return;
    audio.volume = readOutputVolume();
  }, [readOutputVolume]);

  const rampOutputVolume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || seekSilentRef.current) return;
    const token = beginGainOp();
    const target = readOutputVolume();
    void fadeAudioVolume(audio, target, AUDIO_GAIN_FADE_MS, () => isGainActive(token) && !seekSilentRef.current);
  }, [beginGainOp, isGainActive, readOutputVolume]);

  const persistVolume = useCallback((nextVolume: number, nextMuted: boolean) => {
    saveGlobalAudioVolumePrefs({ volume: nextVolume, muted: nextMuted });
  }, []);

  const readPacks = useCallback(
    () => packsRef.current ?? queryClient.getQueryData<FonotekaPack[]>(FONOTEKA_QUERY_KEY),
    [queryClient],
  );

  const hardStop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio.volume = readOutputVolume();
    }
    setIsPlaying(false);
    setCurrentTimeSec(0);
    setDurationSec(0);
    dispatch({ type: 'close' });
  }, [readOutputVolume]);

  const fadeOutIfPlaying = useCallback(
    async (audio: HTMLAudioElement, token: number) => {
      if (audio.paused) {
        audio.volume = 0;
        return;
      }
      if (audio.volume <= 0) return;
      await fadeAudioVolume(audio, 0, AUDIO_FADE_MS, () => isOpActive(token));
      if (!isOpActive(token)) return;
      audio.volume = 0;
    },
    [isOpActive],
  );

  const fadeInPlaying = useCallback(
    async (audio: HTMLAudioElement, token: number) => {
      if (!isOpActive(token)) return;
      // Always start silent so resume after pause cannot click.
      audio.volume = 0;
      try {
        await audio.play();
      } catch {
        if (isOpActive(token)) hardStop();
        return;
      }
      if (!isOpActive(token)) return;
      if (seekSilentRef.current) return;
      await fadeAudioVolume(audio, readOutputVolume(), AUDIO_FADE_MS, () => isOpActive(token) && !seekSilentRef.current);
      if (isOpActive(token) && !seekSilentRef.current) {
        audio.volume = readOutputVolume();
      }
    },
    [hardStop, isOpActive, readOutputVolume],
  );

  const stop = useCallback(() => {
    const token = beginOp();
    const audio = audioRef.current;
    void (async () => {
      if (audio) {
        await fadeOutIfPlaying(audio, token);
        if (!isOpActive(token)) return;
      }
      hardStop();
    })();
  }, [beginOp, fadeOutIfPlaying, hardStop, isOpActive]);

  const loadAndPlay = useCallback(
    (next: GlobalAudioTrack) => {
      const src = next.src.trim();
      if (!src) return;
      const audio = audioRef.current;
      if (!audio) return;
      const token = beginOp();

      void (async () => {
        await fadeOutIfPlaying(audio, token);
        if (!isOpActive(token)) return;
        audio.pause();

        dispatch({ type: 'set-track', track: next });
        setCurrentTimeSec(0);
        setDurationSec(0);
        setIsPlaying(false);

        audio.volume = 0;
        audio.src = src;
        audio.load();
        await fadeInPlaying(audio, token);
      })();
    },
    [beginOp, fadeInPlaying, fadeOutIfPlaying, isOpActive],
  );

  const play = useCallback(
    (next: GlobalAudioTrack, opts?: { force?: boolean; playlist?: GlobalAudioPlaylist }) => {
      const src = next.src.trim();
      if (!src) return;
      const audio = audioRef.current;
      if (!audio) return;
      // Нажатие человека задаёт плейлист заново; переход к соседу (force) остаётся в прежнем
      if (!opts?.force) {
        playlistRef.current = opts?.playlist ?? null;
        setPlaylist(playlistRef.current);
      }

      if (!opts?.force && shouldToggleCurrent(trackRef.current, next)) {
        if (!audio.paused) {
          const token = beginOp();
          setIsPlaying(false);
          void (async () => {
            await fadeOutIfPlaying(audio, token);
            if (!isOpActive(token)) return;
            audio.pause();
            // Keep volume at 0 while paused — restoring gain here causes a click.
            audio.volume = 0;
          })();
          return;
        }
        const token = beginOp();
        void fadeInPlaying(audio, token);
        return;
      }

      loadAndPlay(next);
    },
    [beginOp, fadeInPlaying, fadeOutIfPlaying, isOpActive, loadAndPlay],
  );

  playRef.current = play;

  /** Список, по которому идут соседи: плейлист, откуда включили, иначе вся Фонотека */
  const currentList = useCallback(
    (): readonly GlobalAudioTrack[] => playlistRef.current?.tracks() ?? buildFonotekaPlaylist(readPacks() ?? []),
    [readPacks],
  );

  const playNeighbor = useCallback(
    (direction: 1 | -1) => {
      const current = trackRef.current;
      if (!current || current.source !== 'fonoteka') return;
      const neighbor = neighborInPlaylist(currentList(), current.instanceId, direction);
      if (!neighbor) return;
      play(neighbor, { force: true });
    },
    [play, currentList],
  );

  const playNext = useCallback(() => playNeighbor(1), [playNeighbor]);
  const playPrevious = useCallback(() => playNeighbor(-1), [playNeighbor]);

  const setVolume = useCallback(
    (next: number, opts?: { ramp?: boolean }) => {
      const vol = clamp01(next);
      if (vol === 0) {
        setMutedState(true);
        mutedRef.current = true;
        persistVolume(volumeRef.current > 0 ? volumeRef.current : 0, true);
      } else {
        setVolumeState(vol);
        setMutedState(false);
        volumeRef.current = vol;
        mutedRef.current = false;
        persistVolume(vol, false);
      }
      if (seekSilentRef.current) return;
      if (opts?.ramp === false) {
        applyOutputVolume();
        return;
      }
      rampOutputVolume();
    },
    [applyOutputVolume, persistVolume, rampOutputVolume],
  );

  const toggleMute = useCallback(() => {
    if (!mutedRef.current) {
      setMutedState(true);
      mutedRef.current = true;
      persistVolume(volumeRef.current > 0 ? volumeRef.current : 1, true);
    } else {
      const restore = volumeRef.current > 0 ? volumeRef.current : 1;
      setVolumeState(restore);
      setMutedState(false);
      volumeRef.current = restore;
      mutedRef.current = false;
      persistVolume(restore, false);
    }
    if (seekSilentRef.current) return;
    rampOutputVolume();
  }, [persistVolume, rampOutputVolume]);

  const beginSeekSilence = useCallback(() => {
    const audio = audioRef.current;
    beginGainOp();
    seekSilentRef.current = true;
    if (audio) audio.volume = 0;
  }, [beginGainOp]);

  const endSeekSilence = useCallback(() => {
    seekSilentRef.current = false;
    const audio = audioRef.current;
    if (!audio || audio.paused) {
      // Stay silent while paused; next play fades in.
      if (audio) audio.volume = 0;
      return;
    }
    rampOutputVolume();
  }, [rampOutputVolume]);

  useEffect(() => {
    applyOutputVolume();
  }, [applyOutputVolume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMeta = () => {
      const d = Number(audio.duration);
      if (Number.isFinite(d) && d > 0) setDurationSec(d);
      setCurrentTimeSec(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
    };
    const onTime = () => {
      setCurrentTimeSec(audio.currentTime);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      const current = trackRef.current;
      if (current?.source === 'fonoteka') {
        const next = neighborInPlaylist(currentList(), current.instanceId, 1);
        if (next) {
          playRef.current(next, { force: true });
          return;
        }
      }
      stop();
    };
    const onError = () => {
      beginOp();
      hardStop();
    };

    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('durationchange', onLoadedMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('durationchange', onLoadedMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [beginOp, hardStop, currentList, stop]);

  const toggle = useCallback(() => {
    const current = trackRef.current;
    if (!current) return;
    // Пауза и продолжение из панели плеера — в том же плейлисте
    play(current, { playlist: playlistRef.current ?? undefined });
  }, [play]);

  const seek = useCallback((ratio: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const d = Number(audio.duration);
    if (!Number.isFinite(d) || d <= 0) return;
    audio.currentTime = clamp01(ratio) * d;
    setCurrentTimeSec(audio.currentTime);
  }, []);

  const durationKnown = Number.isFinite(durationSec) && durationSec > 0;
  const progressRatio = durationKnown ? clamp01(currentTimeSec / durationSec) : 0;

  const catalogPosition = useMemo(
    () => (state.track?.source === 'fonoteka' ? fonotekaPlaylistIndex(packs, state.track.instanceId) : null),
    [packs, state.track],
  );
  // Плейлист маленький и живой (его список меняется в кэше без нового рендера плеера) — считается на каждом рендере
  const position =
    state.track?.source !== 'fonoteka' ? null : playlist ? playlistIndex(playlist.tracks(), state.track.instanceId) : catalogPosition;
  const canPlayPrevious = position != null && position.index > 0;
  const canPlayNext = position != null && position.index >= 0 && position.index < position.length - 1;

  const playbackValue = useMemo<GlobalAudioPlaybackApi>(
    () => ({
      track: state.track,
      isPlaying,
      canPlayPrevious,
      canPlayNext,
      play,
      toggle,
      playPrevious,
      playNext,
      seek,
      beginSeekSilence,
      endSeekSilence,
      stop,
    }),
    [
      state.track,
      isPlaying,
      canPlayPrevious,
      canPlayNext,
      play,
      toggle,
      playPrevious,
      playNext,
      seek,
      beginSeekSilence,
      endSeekSilence,
      stop,
    ],
  );

  const clockValue = useMemo<GlobalAudioClockApi>(
    () => ({
      currentTimeSec,
      durationSec,
      progressRatio,
    }),
    [currentTimeSec, durationSec, progressRatio],
  );

  const volumeValue = useMemo<GlobalAudioVolumeApi>(
    () => ({
      volume,
      muted,
      setVolume,
      toggleMute,
    }),
    [volume, muted, setVolume, toggleMute],
  );

  return (
    <GlobalAudioPlaybackContext.Provider value={playbackValue}>
      <GlobalAudioClockContext.Provider value={clockValue}>
        <GlobalAudioVolumeContext.Provider value={volumeValue}>
          <audio ref={audioRef} preload="metadata" />
          {children}
        </GlobalAudioVolumeContext.Provider>
      </GlobalAudioClockContext.Provider>
    </GlobalAudioPlaybackContext.Provider>
  );
}

export function useGlobalAudioPlayback(): GlobalAudioPlaybackApi {
  const ctx = useContext(GlobalAudioPlaybackContext);
  if (!ctx) {
    throw new Error('useGlobalAudioPlayback must be used within GlobalAudioProvider');
  }
  return ctx;
}

export function useGlobalAudioClock(): GlobalAudioClockApi {
  const ctx = useContext(GlobalAudioClockContext);
  if (!ctx) {
    throw new Error('useGlobalAudioClock must be used within GlobalAudioProvider');
  }
  return ctx;
}

export function useGlobalAudioVolume(): GlobalAudioVolumeApi {
  const ctx = useContext(GlobalAudioVolumeContext);
  if (!ctx) {
    throw new Error('useGlobalAudioVolume must be used within GlobalAudioProvider');
  }
  return ctx;
}

/** Broad subscription — prefer the split hooks when possible. */
export function useGlobalAudio(): GlobalAudioApi {
  return {
    ...useGlobalAudioPlayback(),
    ...useGlobalAudioClock(),
    ...useGlobalAudioVolume(),
  };
}
